import { useContext, useEffect } from 'react';

import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import { GlobalContext } from '../../../../../providers/global-provider';
import {
  DATA_DISPLAY_KEYS,
  DataParent,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import {
  STRIKE_REPORT_INITIAL_DATA,
  StrikeReportContext,
} from '../../../../../providers/strike-report';

export const useAggregatedStrikeReportEffect = () => {
  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    aggregatedData,
    getAggregatedData,
    selectedDataDisplayOption,
    selectedAccountsAggregated,
  } = useContext(PortfolioManagementContext);
  const {
    isStrikeReportLoading,
    getStrikeReportData,
    setStrikeReportData,
    strikeReportFilters: {
      strikeReportFilterToken: { token },
      setStrikeReportFilterToken,
    },
  } = useContext(StrikeReportContext);

  const isDataDisplayAggregated = selectedDataDisplayOption === DATA_DISPLAY_KEYS.aggregated;
  const dataParent: DataParent = PORTFOLIO_TABS_NAME.strikeReport as DataParent;

  useEffect(() => {
    if (
      isStrikeReportLoading ||
      !isDataDisplayAggregated ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.strikeReport
    ) {
      return;
    }

    const strikeDataAggregatedAcc = getAggregatedData(dataParent);
    const accounts =
      aggregatedData?.accounts.length > 0 ? aggregatedData?.accounts : selectedAccountsAggregated;

    if (!strikeDataAggregatedAcc) {
      getStrikeReportData({ accounts });
      return;
    }

    if (strikeDataAggregatedAcc.error) {
      setStrikeReportData({
        ...STRIKE_REPORT_INITIAL_DATA,
        isError: true,
      });
      return;
    }

    const report = strikeDataAggregatedAcc?.data?.report;
    if (!report) {
      setStrikeReportData(STRIKE_REPORT_INITIAL_DATA);
      return;
    }

    const tokenKeys = Object.keys(report).sort((a, b) => a.localeCompare(b));
    if (tokenKeys.length === 0) {
      setStrikeReportData(STRIKE_REPORT_INITIAL_DATA);
      return;
    }

    const currentToken = tokenKeys.includes(token) ? token : tokenKeys[0];
    const reportDataToken = report[currentToken];

    if (strikeDataAggregatedAcc.data && reportDataToken) {
      setStrikeReportData({
        data: reportDataToken,
        lastUpdate: strikeDataAggregatedAcc.lastUpdate,
        isError: false,
      });

      setStrikeReportFilterToken((state: any) => ({
        ...state,
        token: currentToken,
        tokenList: tokenKeys.map((tokenKey) => ({
          value: tokenKey,
          name: tokenKey,
          isVisible: true,
          isDefault: tokenKey === currentToken,
        })),
      }));
      return;
    }

    setStrikeReportData(STRIKE_REPORT_INITIAL_DATA);
  }, [isStrikeReportLoading, portfolioCurrentTab, isDataDisplayAggregated, token]);
};
